import AccessoryTypePanel from 'components/accessories-page/accessory-type-panel';
import AdditionalAccessoriesDrawer from 'components/accessories-page/additional-accessories-drawer';
import LoadingSpinner from 'components/common/loading-spinner';
import {
  AccessoryProductType,
  AccessoryWithQuantityType,
  SelectedSystemType
} from 'data/types';
import { useDuctedAccessoriesPageData } from 'hooks/use-ducted-accessories-page-data';
import { useDuctedAccessoriesWriter } from 'hooks/use-ducted-accessories-writer';
import { useManualAccessories } from 'hooks/use-manual-accessories';
import { useEffect, useMemo } from 'react';
import type {
  AccessoryQuantityChangeType,
  AccessorySelectionChangeType
} from 'utils/accessory-option-groups-types';
import {
  applyRequiredPick,
  findRecommendedLineSetProductId,
  findSelectedLineSetProductId,
  replaceLineSet,
  toRequiredAccessoryGroups
} from 'utils/ducted-accessories';
import {
  applyDuctedSelectionChanges,
  ductedSelectionEntries,
  hasDuctedAccessorySelections,
  hydrateDuctedSelectionsFromSavedRows,
  selectedAccessoriesFor,
  withEquipmentAccessories,
  withSystemAccessories
} from 'utils/ducted-accessory-selections';
import type { DuctedLineSetScopeType } from 'utils/ducted-accessories-types';
import {
  AccessoryPick,
  picksMatchingProductIds,
  quotableAccessoryProductIds
} from 'utils/ducted-accessory-groups';
import DuctedLineSetSelect from './ducted-line-set-select';
import DuctedRequiredAccessories from './ducted-required-accessories';

type DuctedAccessoriesPageProps = {
  system: SelectedSystemType;
};

export default function DuctedAccessoriesPage({
  system
}: DuctedAccessoriesPageProps) {
  const {
    recommendations,
    lineSetScopes,
    selections,
    panels,
    requiredOptions,
    savedSystem,
    isLoading
  } = useDuctedAccessoriesPageData(system);
  const { writeSelections, applySelections } =
    useDuctedAccessoriesWriter(system);

  const requiredGroups = useMemo(
    () => toRequiredAccessoryGroups(requiredOptions ?? []),
    [requiredOptions]
  );

  const systemAccessories = selections.system;

  useEffect(() => {
    if (isLoading || system.quotableAccessories.length > 0) return;

    const savedRows = savedSystem?.accessories ?? [];
    if (savedRows.length === 0) return;

    const hydrated = hydrateDuctedSelectionsFromSavedRows({
      savedRows,
      recommendations,
      requiredGroups: requiredOptions ?? [],
      system
    });
    if (!hasDuctedAccessorySelections(hydrated)) return;
    writeSelections(hydrated, false);
  }, [
    isLoading,
    savedSystem,
    system,
    recommendations,
    requiredOptions,
    writeSelections
  ]);

  const picks = useMemo(
    () =>
      picksMatchingProductIds(
        requiredGroups,
        quotableAccessoryProductIds(system)
      ),
    [requiredGroups, system]
  );

  const applySystemAccessories = (next: AccessoryWithQuantityType[]) => {
    applySelections(withSystemAccessories(selections, next));
  };

  const selectedAccessories = useMemo(
    () => ductedSelectionEntries(selections),
    [selections]
  );
  const { manualAccessories, selectedProductIds, handleManualAccessoryChange } =
    useManualAccessories({
      quotableSystemUniqueId: system.uniqueId,
      accessories: selectedAccessories,
      onChange: (product, quantity, ownerUniqueId) => {
        applySelections(
          applyDuctedSelectionChanges(selections, [
            {
              equipmentUniqueId: ownerUniqueId ?? selections.systemUniqueId,
              accessory: product,
              quantity
            }
          ])
        );
      }
    });

  const handleLineSetChange = (
    scope: DuctedLineSetScopeType,
    productId: number | null
  ) => {
    applySelections(
      withEquipmentAccessories(
        selections,
        scope.equipmentUniqueId,
        replaceLineSet(
          selectedAccessoriesFor(selections, scope.equipmentUniqueId),
          scope.options,
          productId
        )
      )
    );
  };

  const handleRequiredPick = (
    accessoryTypeCode: string,
    pick: AccessoryPick
  ) => {
    const group = requiredGroups.find(
      (candidate) => candidate.accessoryTypeCode === accessoryTypeCode
    );
    if (!group) return;
    applySystemAccessories(applyRequiredPick(systemAccessories, group, pick));
  };

  const handleAccessoryChange = (
    condenserUniqueId: string,
    accessory: AccessoryProductType,
    quantity: number,
    equipmentUniqueId?: string
  ) => {
    applySelections(
      applyDuctedSelectionChanges(selections, [
        {
          equipmentUniqueId: equipmentUniqueId ?? condenserUniqueId,
          accessory,
          quantity
        }
      ])
    );
  };

  const handleBatchAccessoryChange = (
    condenserUniqueId: string,
    changes: AccessorySelectionChangeType[],
    equipmentUniqueId?: string
  ) => {
    applySelections(
      applyDuctedSelectionChanges(
        selections,
        changes.map((change) => ({
          ...change,
          equipmentUniqueId: equipmentUniqueId ?? condenserUniqueId
        }))
      )
    );
  };

  const handleIncludeRecommendedToggle = (
    _accessoryTypeCode: string,
    _includeRecommended: boolean,
    changes: AccessoryQuantityChangeType[]
  ) => {
    applySelections(applyDuctedSelectionChanges(selections, changes));
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <LoadingSpinner className='h-8 w-8 animate-spin text-gray-400' />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-5'>
      {lineSetScopes
        .filter((scope) => scope.options.length > 0)
        .map((scope, index) => (
          <div
            key={scope.equipmentUniqueId}
            id={index === 0 ? 'line-set-drawer' : undefined}
          >
            <DuctedLineSetSelect
              condenser={scope.condenser}
              options={scope.options}
              selectedProductId={findSelectedLineSetProductId(
                selectedAccessoriesFor(selections, scope.equipmentUniqueId),
                scope.options
              )}
              recommendedProductId={findRecommendedLineSetProductId(
                scope.options
              )}
              requiredLengthFeet={scope.requiredLengthFeet}
              onChange={(productId) => handleLineSetChange(scope, productId)}
            />
          </div>
        ))}
      <DuctedRequiredAccessories
        systemUniqueId={system.uniqueId}
        groups={requiredGroups}
        picks={picks}
        onPick={handleRequiredPick}
      />
      {panels.map((panel, index) => (
        <div
          key={panel.accessoryType.code}
          id={index === 0 ? 'first-accessory-section' : undefined}
        >
          <AccessoryTypePanel
            accessoryType={panel.accessoryType}
            equipment={panel.equipment}
            onAccessoryChange={handleAccessoryChange}
            onBatchAccessoryChange={handleBatchAccessoryChange}
            onIncludeRecommendedToggle={handleIncludeRecommendedToggle}
            isCompleted={panel.isCompleted}
          />
        </div>
      ))}
      <AdditionalAccessoriesDrawer
        accessories={manualAccessories}
        selectedProductIds={selectedProductIds}
        onChange={handleManualAccessoryChange}
      />
    </div>
  );
}
